import React from "react";
import {
  Box,
  Chip,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  delete_active_icon,
  delete_disabled_icon,
  down_sort_arrow_icon,
  download_active_icon,
  download_disabled_icon,
  edit_active_icon,
  edit_disabled_icon,
  list_eval_disabled_icon,
  list_eval_icon,
  up_sort_arrow_icon,
} from "../../constants/icons";
import IconButtons from "../IconButtons/IconButtons";
import TablePagination from "../TablePagination/TablePagination";
import {
  evaluationStatusChip,
  font_13_icon,
  font_15,
  font_9,
  ROLE_TYPES,
} from "../../utils/utils";
import { backendBaseUrl } from "../../config/config";
import {
  apiBasePath,
  sortInAscending,
  sortInDescending,
} from "../../constants/constants";
import dayjs from "dayjs";
import { Link, useParams } from "react-router-dom";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";
import { useSelector } from "react-redux";

// Helper function to get sort icon size
const getSortIconSize = (isActive) => (isActive ? null : font_9);

// Component for Sortable Column Header
const SortableColumnHeader = ({
  label,
  sortKey,
  sortBy,
  orderBy,
  handleSorting,
  minWidth,
  align = "left",
}) => {
  const isAscActive = sortBy === sortKey && orderBy === "ASC";
  const isDescActive = sortBy === sortKey && orderBy === "DESC";
  // Special case: Title column only shows DESC icon when not active or not DESC
  const showDescIcon =
    sortKey !== "title" || sortBy !== sortKey || orderBy !== "DESC";

  return (
    <TableCell align={align} sx={{ minWidth }} className="padding-left-1">
      <Typography variant="font_13_bold" className="flex-table-sort-head">
        <Box className="table-sort-head">{label}</Box>
        <Box className="table-sort-icon">
          <IconButtons
            height={getSortIconSize(isAscActive)}
            width={getSortIconSize(isAscActive)}
            image={up_sort_arrow_icon}
            handleClick={() => handleSorting("ASC", sortKey)}
            disabled={isAscActive}
            tooltip={sortInAscending}
          />
          {showDescIcon && (
            <IconButtons
              height={getSortIconSize(isDescActive)}
              width={getSortIconSize(isDescActive)}
              image={down_sort_arrow_icon}
              handleClick={() => handleSorting("DESC", sortKey)}
              disabled={isDescActive}
              tooltip={sortInDescending}
            />
          )}
        </Box>
      </Typography>
    </TableCell>
  );
};

// Helper function to get toggle tooltip title
const getToggleTooltipTitle = (
  canToggleActive,
  toggleActiveMessage,
  active
) => {
  if (!canToggleActive) {
    return toggleActiveMessage;
  }
  return active ? "Inactivate Assessment" : "Activate Assessment";
};

// Helper function to get evaluation list URL
const getEvaluationListUrl = (
  canListEvaluations,
  moduleId,
  bootcampId,
  assessmentId
) => {
  if (!canListEvaluations) {
    return undefined;
  }
  if (moduleId) {
    return `/bootcamp/${bootcampId}/module/${moduleId}/assessment/${assessmentId}/evaluations`;
  }
  return `/bootcamp/${bootcampId}/assessment/${assessmentId}/evaluations`;
};

// Component for Action Buttons
const ActionButtons = ({
  permissions,
  assessmentId,
  assessment,
  active,
  bootcampId,
  moduleId,
  handleEditAssessment,
  handleActivateInactivateAssessment,
  handleDeleteAssessment,
  handleDownloadIconClick,
}) => {
  const {
    canViewToggleActive,
    canToggleActive,
    toggleActiveMessage,
    canViewDeleteAssessment,
    canDeleteAssessment,
    deleteAssessmentMessage,
    canViewEditAssessment,
    canEditAssessment,
    editAssessmentMessage,
    canViewListEvaluations,
    canListEvaluations,
    listEvaluationsMessage,
  } = permissions;

  const toggleTitle = getToggleTooltipTitle(
    canToggleActive,
    toggleActiveMessage,
    active
  );
  const evaluationListUrl = getEvaluationListUrl(
    canListEvaluations,
    moduleId,
    bootcampId,
    assessmentId
  );

  return (
    <Box className="action-flex">
      {canViewToggleActive && (
        <BootstrapTooltip title={toggleTitle}>
          <Box>
            <Switch
              className="margin-right-2"
              disabled={!canToggleActive}
              checked={active || false}
              size="small"
              onChange={(event) =>
                handleActivateInactivateAssessment(event, assessmentId, active)
              }
              sx={{
                margin: "0",
                "& .MuiSwitch-thumb": {
                  width: 10,
                  height: 10,
                },
              }}
            />
          </Box>
        </BootstrapTooltip>
      )}
      {canViewDeleteAssessment && (
        <IconButtons
          tooltip="Delete Assessment"
          width={font_13_icon}
          height={font_13_icon}
          image={delete_active_icon}
          handleClick={() => handleDeleteAssessment(assessmentId)}
          classList="margin-right-1"
          disabled={!canDeleteAssessment}
          disabledImage={delete_disabled_icon}
          disabledTooltip={
            !canDeleteAssessment ? deleteAssessmentMessage : null
          }
        />
      )}
      {canViewEditAssessment && (
        <IconButtons
          tooltip="Edit Assessment"
          width={font_13_icon}
          height={font_13_icon}
          image={edit_active_icon}
          handleClick={(event) => handleEditAssessment(event, assessment)}
          classList="margin-right-1"
          disabled={!canEditAssessment}
          disabledImage={edit_disabled_icon}
          disabledTooltip={!canEditAssessment ? editAssessmentMessage : null}
        />
      )}
      {canViewListEvaluations && (
        <Link
          to={evaluationListUrl}
          style={{
            lineHeight: 0,
            cursor: "default",
          }}
        >
          <IconButtons
            width={font_15}
            height={font_15}
            image={list_eval_icon}
            tooltip="List Evaluation"
            disabled={!canListEvaluations}
            classList="margin-right-1"
            disabledImage={list_eval_disabled_icon}
            disabledTooltip={
              !canListEvaluations ? listEvaluationsMessage : null
            }
          />
        </Link>
      )}
      {canViewEditAssessment && (
        <IconButtons
          size="small"
          height={font_13_icon}
          width={font_13_icon}
          image={download_active_icon}
          disabledImage={download_disabled_icon}
          handleClick={handleDownloadIconClick.bind(this, assessmentId)}
          tooltip="Download Assessment Report"
        />
      )}
    </Box>
  );
};

// Component for Assessment Table Row
const AssessmentTableRow = ({
  assessment,
  viaModule,
  bootcampId,
  moduleId,
  handleEditAssessment,
  handleActivateInactivateAssessment,
  handleDeleteAssessment,
  handleDownloadIconClick,
}) => {
  const active = assessment?.active;
  const assessmentId = assessment?.id;
  const title = assessment?.title;
  const type = assessment?.type;
  const startDate = assessment?.activeFromDate;
  const endDate = assessment?.activeToDate;
  const durationInMinutes = assessment?.durationInMinutes;
  const module = assessment?.module;
  const permissions = assessment?.permission || {};
  const status = evaluationStatusChip(assessment?.status);
  const { canViewAssessment } = permissions;

  return (
    <TableRow className="table-body-row" hover key={assessmentId}>
      <TableCell align="left" className="padding-left-1">
        <Typography
          color={canViewAssessment && "secondary"}
          variant="font_12"
          className={canViewAssessment && "cursor-pointer"}
        >
          {canViewAssessment ? (
            <BootstrapTooltip title={"Show Assessment"}>
              <Link
                to={
                  moduleId
                    ? `/bootcamp/${bootcampId}/module/${moduleId}/assessment/${assessmentId}`
                    : `/bootcamp/${bootcampId}/assessment/${assessmentId}`
                }
              >
                <u>{title}</u>
              </Link>
            </BootstrapTooltip>
          ) : (
            title
          )}
        </Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">{type}</Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">
          {dayjs(startDate)?.format("DD/MM/YYYY hh:mm A")}
        </Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">
          {dayjs(endDate)?.format("DD/MM/YYYY hh:mm A")}
        </Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">
          {durationInMinutes ? `${durationInMinutes} mins` : "-"}
        </Typography>
      </TableCell>
      {!viaModule && (
        <TableCell align="left" className="padding-left-1">
          <BootstrapTooltip title="View Module">
            <Typography variant="font_12" color="secondary">
              <Link to={`/bootcamp/${bootcampId}/module/${module?.id}`}>
                <u>{module?.name}</u>
              </Link>
            </Typography>
          </BootstrapTooltip>
        </TableCell>
      )}
      <TableCell className="padding-left-1" sx={{ minWidth: "6rem" }}>
        <Chip
          className="same-size-chip"
          label={status?.name}
          variant={status?.theme}
        />
      </TableCell>
      <TableCell align="center" className="padding-left-1 padding-right-2">
        <ActionButtons
          permissions={permissions}
          assessmentId={assessmentId}
          assessment={assessment}
          active={active}
          bootcampId={bootcampId}
          moduleId={moduleId}
          handleEditAssessment={handleEditAssessment}
          handleActivateInactivateAssessment={
            handleActivateInactivateAssessment
          }
          handleDeleteAssessment={handleDeleteAssessment}
          handleDownloadIconClick={handleDownloadIconClick}
        />
      </TableCell>
    </TableRow>
  );
};

const AssessmentListingTable = ({
  assessmentList,
  totalPages,
  handlePageChange,
  currentPage,
  handleSorting,
  sortBy,
  orderBy,
  viaModule,
  totalResult,
  handleEditAssessment,
  handleActivateInactivateAssessment,
  handleDeleteAssessment,
}) => {
  const params = useParams();
  const { bootcampId, moduleId } = params;

  const security = useSelector((state) => state?.security);
  const roles = security?.roles;
  const isHr = ROLE_TYPES?.isHr(roles);

  const downloadFile = (data) => {
    window.open(
      `${backendBaseUrl}${apiBasePath}/assessment/${data?.assessmentId}/report`,
      "_blank"
    );
  };

  const handleDownloadIconClick = (assessmentId) => {
    if (!isHr) {
      downloadFile({ assessmentId });
    }
  };

  return (
    <>
      <TableContainer className="table-container">
        <Table stickyHeader>
          <TableHead className="table-head-container">
            <TableRow className="table-head-row" sx={{ height: "2.711rem" }}>
              <SortableColumnHeader
                label="Title"
                sortKey="title"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="13rem"
              />
              <SortableColumnHeader
                label="Type"
                sortKey="type"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="8rem"
              />
              <SortableColumnHeader
                label="Start Date"
                sortKey="activeFromDate"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="9rem"
              />
              <SortableColumnHeader
                label="End Date"
                sortKey="activeToDate"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="9rem"
              />
              <TableCell
                align="left"
                sx={{ minWidth: "8rem" }}
                className="padding-left-1"
              >
                <Typography
                  variant="font_13_bold"
                  className="flex-table-sort-head"
                >
                  Duration
                </Typography>
              </TableCell>
              {!viaModule && (
                <SortableColumnHeader
                  label="Module"
                  sortKey="moduleName"
                  sortBy={sortBy}
                  orderBy={orderBy}
                  handleSorting={handleSorting}
                  minWidth="10rem"
                  align="center"
                />
              )}
              <TableCell className="padding-left-1" sx={{ minWidth: "6rem" }}>
                <Typography variant="font_13_bold">Status</Typography>
              </TableCell>
              <TableCell
                align="center"
                sx={{ minWidth: "10rem", maxWidth: "10rem", width: "7rem" }}
                className="padding-left-1 padding-right-2"
              >
                <Typography variant="font_13_bold">Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assessmentList?.map((assessment) => (
              <AssessmentTableRow
                key={assessment?.id}
                assessment={assessment}
                viaModule={viaModule}
                bootcampId={bootcampId}
                moduleId={moduleId}
                handleEditAssessment={handleEditAssessment}
                handleActivateInactivateAssessment={
                  handleActivateInactivateAssessment
                }
                handleDeleteAssessment={handleDeleteAssessment}
                handleDownloadIconClick={handleDownloadIconClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container className="table-pagination-container">
        <Typography variant="font_16">Total {totalResult} Records</Typography>
        <TablePagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          currentPage={currentPage}
        />
      </Grid>
    </>
  );
};

export default AssessmentListingTable;
